import { Layout } from "../components/Layout"
import { useDashboard } from "@/hooks/useDashboard"
import { CardSkeleton } from "@/components/Skeleton/Skeleton"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import {
  UsersIcon,
  UserGroupIcon,
  FilmIcon,
  ServerIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from "@heroicons/react/24/outline"
import { Link } from "react-router-dom"
import { StatementsNeedingAttention } from "@/components/StatementsNeedingAttention"

export function Dashboard() {
  const { data: dashboard, isLoading, error } = useDashboard()

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-primary">Dashboard</h1>
          <p className="text-secondary">Platform overview and statistics</p>
        </div>

        <StatementsNeedingAttention />

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {isLoading ? (
            <>
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </>
          ) : (
            <>
              <StatCard
                title="Total Users"
                value={dashboard?.stats.users.total || 0}
                subValue={`${dashboard?.stats.users.active || 0} active`}
                icon={UsersIcon}
                href="/users"
              />
              <StatCard
                title="Teams"
                value={dashboard?.stats.teams.total || 0}
                subValue={`${dashboard?.stats.teams.active || 0} active`}
                icon={UserGroupIcon}
                href="/teams"
              />
              <StatCard
                title="Clips"
                value={dashboard?.stats.clips.total || 0}
                subValue={`${dashboard?.stats.games.total || 0} games`}
                icon={FilmIcon}
                href="/clips"
              />
              {dashboard?.storage.available ? (
                <StatCard
                  title="Storage Used"
                  value={`${dashboard.storage.usedGB?.toFixed(1)} GB`}
                  subValue={`${(dashboard.storage.fileCount ?? 0).toLocaleString()} files${dashboard.storage.partial ? " (partial listing)" : ""}`}
                  icon={ServerIcon}
                  href="/resources/storage"
                />
              ) : (
                <StatCard
                  title="Storage"
                  value="Not measured yet"
                  subValue="Press Refresh breakdown on the Storage page"
                  icon={ServerIcon}
                  href="/resources/storage"
                />
              )}
            </>
          )}
        </div>

        {/* Recent Activity and Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Activity */}
          <div className="bg-surface rounded-lg border border-border p-6">
            <h2 className="text-lg font-semibold mb-4">Recent Activity</h2>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center space-x-3">
                    <div className="w-2 h-2 rounded-full bg-secondary/20 animate-pulse" />
                    <div className="flex-1 h-4 bg-secondary/20 rounded animate-pulse" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {dashboard?.activity.recent.map((activity, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-1.5" />
                    <div className="flex-1">
                      <p className="text-sm">{activity.description}</p>
                      <p className="text-xs text-secondary mt-1">
                        {formatTime(activity.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-surface rounded-lg border border-border p-6">
            <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
            <div className="space-y-3">
              <Link
                to="/users"
                className="block p-3 rounded-md border border-border hover:bg-background transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <UsersIcon className="h-5 w-5 text-secondary" />
                    <span className="text-sm font-medium">Manage Users</span>
                  </div>
                  <span className="text-sm text-secondary">→</span>
                </div>
              </Link>
              <Link
                to="/teams"
                className="block p-3 rounded-md border border-border hover:bg-background transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <UserGroupIcon className="h-5 w-5 text-secondary" />
                    <span className="text-sm font-medium">Manage Teams</span>
                  </div>
                  <span className="text-sm text-secondary">→</span>
                </div>
              </Link>
              <Link
                to="/search"
                className="block p-3 rounded-md border border-border hover:bg-background transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <FilmIcon className="h-5 w-5 text-secondary" />
                    <span className="text-sm font-medium">Search Content</span>
                  </div>
                  <span className="text-sm text-secondary">→</span>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

interface StatCardProps {
  title: string
  value: number | string
  subValue: string
  icon: React.ComponentType<{ className?: string }>
  trend?: {
    value: number
    isPositive: boolean
  }
  href?: string
}

function StatCard({ title, value, subValue, icon: Icon, trend, href }: StatCardProps) {
  const content = (
    <>
      <div className="flex items-center justify-between">
        <Icon className="h-8 w-8 text-secondary" />
        {trend && (
          <div
            className={`flex items-center text-sm ${
              trend.isPositive ? "text-green-600" : "text-red-600"
            }`}
          >
            {trend.isPositive ? (
              <ArrowUpIcon className="h-4 w-4 mr-1" />
            ) : (
              <ArrowDownIcon className="h-4 w-4 mr-1" />
            )}
            {trend.value}%
          </div>
        )}
      </div>
      <div className="mt-4">
        <h3 className="text-sm font-medium text-secondary">{title}</h3>
        <p className="text-2xl font-semibold mt-1">{value}</p>
        <p className="text-sm text-secondary mt-1">{subValue}</p>
      </div>
    </>
  )

  if (href) {
    return (
      <Link
        to={href}
        className="block bg-surface p-6 rounded-lg border border-border hover:border-primary/50 transition-colors"
      >
        {content}
      </Link>
    )
  }

  return (
    <div className="bg-surface p-6 rounded-lg border border-border">
      {content}
    </div>
  )
}

function formatTime(timestamp: string): string {
  const date = new Date(timestamp)
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return "Just now"
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  return `${days}d ago`
}